// VA Role-based permissions system

// Base roles (no extra permissions)
const BASE_VA_ROLES = ['Threads VA', 'IG VA', 'FB VA', 'X VA', 'Chatting VA'];

// Role permissions mapping
const ROLE_PERMISSIONS = {
  // Admin has access to everything
  'admin': {
    canAccessVAs: true,
    canAccessLinks: true,
    canAccessAnalytics: true,
    canAccessPayments: true,
    canAccessSettings: true,
    canAccessEmails: true,
    canAccessNumbers: true,
    canAccessGeneration: true,
    vaFilter: null, // null means all VAs
  },
  
  // Team Leader VA: Can manage only Threads VAs
  'Team Leader VA': {
    canAccessVAs: true,
    canAccessLinks: true,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: true,
    canAccessNumbers: true,
    vaFilter: ['Threads VA'], // Can only see Threads VAs
  },
  
  // Manager Chatting VA: Can manage only Chatting VAs
  'Manager Chatting VA': {
    canAccessVAs: true,
    canAccessLinks: false,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: false,
    canAccessNumbers: false,
    vaFilter: ['Chatting VA'], // Can only see Chatting VAs
  },
  
  // Head Manager VA: Can manage all VAs except Chatting VAs
  'Head Manager VA': {
    canAccessVAs: true,
    canAccessLinks: true,
    canAccessAnalytics: true,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: true,
    canAccessNumbers: true,
    vaFilter: ['Threads VA', 'IG VA', 'FB VA', 'X VA', 'Team Leader VA', 'Head Manager VA', 'Account Creation VA'], // All except Chatting
  },
  
  // Base VA roles - no extra permissions
  'Threads VA': {
    canAccessVAs: false,
    canAccessLinks: false,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: false,
    canAccessNumbers: false,
    vaFilter: null,
  },
  'IG VA': {
    canAccessVAs: false,
    canAccessLinks: false,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: false,
    canAccessNumbers: false,
    canAccessGeneration: true,
    vaFilter: null,
  },
  'FB VA': {
    canAccessVAs: false,
    canAccessLinks: false,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: false,
    canAccessNumbers: false,
    canAccessGeneration: true,
    vaFilter: null,
  },
  'X VA': {
    canAccessVAs: false,
    canAccessLinks: false,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: false,
    canAccessNumbers: false,
    vaFilter: null,
  },
  'Chatting VA': {
    canAccessVAs: false,
    canAccessLinks: false,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: false,
    canAccessNumbers: false,
    vaFilter: null,
  },
  
  // Account Creation VA: Can view assigned emails and access Numbers
  'Account Creation VA': {
    canAccessVAs: false,
    canAccessLinks: false,
    canAccessAnalytics: false,
    canAccessPayments: false,
    canAccessSettings: false,
    canAccessEmails: true,  // Can VIEW assigned emails only (not manage)
    canAccessNumbers: true,
    vaFilter: null,
  },
};

/**
 * Get permissions for a user based on their role
 * @param {Object} user - User object with role and optionally va_role
 * @returns {Object} Permissions object
 */
export const getPermissions = (user) => {
  if (!user) return ROLE_PERMISSIONS['Threads VA']; // Default minimal permissions
  
  // Admin always has full access
  if (user.role === 'admin') {
    return ROLE_PERMISSIONS['admin'];
  }
  
  // For VA users, check their va_role
  const vaRole = user.va_role || 'Threads VA';
  return ROLE_PERMISSIONS[vaRole] || ROLE_PERMISSIONS['Threads VA'];
};

/**
 * Check if user can access a specific feature
 * @param {Object} user - User object
 * @param {string} feature - Feature name (vas, links, analytics, payments, settings)
 * @returns {boolean}
 */
export const canAccess = (user, feature) => {
  const permissions = getPermissions(user);
  
  switch (feature) {
    case 'vas':
      return permissions.canAccessVAs;
    case 'links':
      return permissions.canAccessLinks;
    case 'analytics':
      return permissions.canAccessAnalytics;
    case 'payments':
      return permissions.canAccessPayments;
    case 'settings':
      return permissions.canAccessSettings;
    case 'emails':
      return permissions.canAccessEmails;
    case 'numbers':
      return permissions.canAccessNumbers;
    case 'generation':
      return permissions.canAccessGeneration;
    default:
      return false;
  }
};

/**
 * Get the VA roles filter for a user
 * @param {Object} user - User object
 * @returns {Array|null} Array of allowed VA roles or null for all
 */
export const getVaRoleFilter = (user) => {
  const permissions = getPermissions(user);
  return permissions.vaFilter;
};

/**
 * Check if user is a base VA (no management permissions)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isBaseVA = (user) => {
  if (!user || user.role === 'admin') return false;
  return BASE_VA_ROLES.includes(user.va_role);
};

export { BASE_VA_ROLES, ROLE_PERMISSIONS };
